import React from "react";
import { useState, useEffect } from "react";
import { useToast } from "../../components/ui/use-toast";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Search, Download, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  questionsAPI,
  categoriesAPI,
  questionTypesAPI,
  questionBankAPI,
} from "../../services/api";

// Import the components
import UploadQuestionBanksModal from "./question-banks/UploadQuestionBanksModal";
import QuestionBankSection from "./question-banks/QuestionBankSection";
import CreateQuestionBankModal from "./question-banks/CreateQuestionBankModal";
import SampleDownloadModal from "./question-banks/SampleDownloadModal";
import DeleteConfirmationDialog from "./question-banks/DeleteConfirmationDialog";

export default function QuestionDetails() {
  const { toast } = useToast();
  const navigate = useNavigate();

  // State management
  const [userCategories, setUserCategories] = useState([]);
  const [companyCategories, setCompanyCategories] = useState([]);
  const [userCompanyQuestionBank, setUserCompanyQuestionBank] = useState([]);
  const [purchasedQuestionBanks, setPurchasedQuestionBanks] = useState([]);
  const [dropDownQuestionBank, setDropDownQuestionBank] = useState([]);
  const [showPurchasedCard, setShowPurchasedCard] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleteBankName, setDeleteBankName] = useState("");
  const [showUserCard, setShowUserCard] = useState(true);
  const [showCompanyCard, setShowCompanyCard] = useState(false);
  const [questionBankCreatedUser, setQuestionBankCreatedUser] = useState([]);
  const [uniqueUsers, setuniqueUsers] = useState([]);
  const [searchQuestionBank, setSearchQuestionBank] = useState([]);
  const [companyUser, setCompanyUser] = useState([]);
  const [purchasedUser, setPurchasedUser] = useState([]);
  const [searchCompanyQuestionBank, setSearchCompanyQuestionBank] = useState(
    []
  );
  const [searchUserQuestionBank, setSearchUserQuestionBank] = useState([]);
  const [searchPurchasedQuestionBank, setSearchPurchasedQuestionBank] =
    useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [questionBanks, setQuestionBanks] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [samplePopupOpen, setSamplePopupOpen] = useState(false);
  const [uploadPopupOpen, setUploadPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isOverlayLoading, setIsOverlayLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Pagination states for each section
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [companyCurrentPage, setCompanyCurrentPage] = useState(1);
  const [purchasedCurrentPage, setPurchasedCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [search, setSearch] = useState("");

  // ... keep existing code (utility functions and API functions remain the same)
  const showToast = (type, title, description) => {
    toast({
      title,
      description,
      variant: type === "error" ? "destructive" : undefined,
    });
  };

  const convertISOtoLocale = (value) => {
    const date = new Date(value);
    return date.toLocaleString();
  };

  const searchQuestionBanks = async () => {
    try {
      setIsOverlayLoading(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      params.append("page", 1);
      params.append("limit", 1000);

      const response = await questionBankAPI.search(params);
      setSearchQuestionBank(response.data.banks || []);
      setSearchCompanyQuestionBank(response.data.companyBanks || []);
      setSearchPurchasedQuestionBank(response.data.purchasedBanks || []);
      setSearchUserQuestionBank(response.data.responseUser || []);
    } catch (error) {
      console.error("Failed to fetch questions:", error);
      showToast("error", "Error", "Failed to search questions");
    } finally {
      setIsOverlayLoading(false);
    }
  };

  const fetchCategoriesForDownload = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setUserCategories(response.data.userCategories || []);
      setCompanyCategories(response.data.companyCategories || []);
    } catch (error) {
      console.error("Error fetching categories for download:", error);
      toast({
        title: "Error",
        description: "Failed to fetch categories",
        variant: "destructive",
      });
    }
  };

  const fetchFilteredCategories = async (questionBankName) => {
    try {
      const response = await categoriesAPI.getFiltered(questionBankName);
      setUserCategories(response.data.userCategories || []);
      setCompanyCategories(response.data.companyCategories || []);
    } catch (error) {
      console.error("Error fetching filtered categories:", error);
      toast({
        title: "Error",
        description: "Failed to fetch filtered categories",
        variant: "destructive",
      });
    }
  };

  const fetchQuestionTypes = async () => {
    try {
      const response = await questionTypesAPI.getAll();
      setQuestionTypes(response.data.questionTypes || []);
    } catch (error) {
      console.error("Failed to fetch question types:", error);
      showToast("error", "Error", "Failed to load question types");
    }
  };

  const fetchQuestionBank = async () => {
    try {
      setIsOverlayLoading(true);
      const status = "draft";
      const params = {
        status,
        page: 1,
        limit: 1000,
        search: search,
      };

      const response = await questionBankAPI.getAll(params);
      const {
        user,
        banks,
        companyQuestionBanks,
        purchasedQuestionBanks,
        companyUser,
        allUser,
        purchasedUser,
        companyDropDown,
      } = response.data;

      setuniqueUsers(allUser);
      setQuestionBankCreatedUser(user || []);
      setCompanyUser(companyUser || []);
      setPurchasedUser(purchasedUser || []);
      setQuestionBanks(banks || []);
      setUserCompanyQuestionBank(companyQuestionBanks || []);
      setPurchasedQuestionBanks(purchasedQuestionBanks || []);
      setDropDownQuestionBank(companyDropDown || []);
    } catch (error) {
      console.error("Failed to fetch question banks:", error);
      showToast("error", "Error", "Failed to load question banks");
    } finally {
      setIsOverlayLoading(false);
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      const response = await questionBankAPI.delete(deleteId);
      if (response.status === 200) {
        fetchQuestionBank();
        setShowDeletePopup(false);
        showToast(
          "success",
          "Success",
          "Question bank and all related questions deleted successfully"
        );
      }
    } catch (error) {
      console.error("Failed to delete question bank:", error);
      showToast(
        "error",
        "Error",
        error?.response?.data?.message || "Failed to delete question bank"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  // Effect hooks
  useEffect(() => {
    if (isSearching && search.length > 0) {
      searchQuestionBanks();
    } else {
      fetchQuestionBank();
    }
    fetchQuestionTypes();
  }, [search, isSearching]);

  useEffect(() => {
    if (search.length === 0) {
      setIsSearching(false);
    }
  }, [search]);

  const handleDownloadSample = () => {
    fetchCategoriesForDownload();
    setSamplePopupOpen(true);
  };

  const handleCreateQuestionBank = () => {
    fetchFilteredCategories();
    setShowCreatePopup(true);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setIsSearching(value.length > 0);
    // Reset pagination when searching
    setUserCurrentPage(1);
    setCompanyCurrentPage(1);
    setPurchasedCurrentPage(1);
  };

  const handleUploadSuccess = () => {
    fetchQuestionBank();
  };

  // Main render
  return (
    <div className="relative">
      {/* Overlay Loading */}
      {isOverlayLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin h-6 w-6 border-t-2 border-b-2 border-blue-500 rounded-full"></div>
            <span className="text-lg font-medium">Loading...</span>
          </div>
        </div>
      )}

      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold">Question Banks</h1>
          <p className="text-gray-500">Manage your assessment question banks</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => handleDownloadSample()}>
            <Download className="h-4 w-4 mr-1" />
            Download Sample
          </Button>
          <Button onClick={() => setUploadPopupOpen(true)}>
            <Upload className="h-4 w-4 mr-1" />
            Upload Question Banks
          </Button>
          <Button onClick={() => handleCreateQuestionBank()}>
            <Plus className="h-4 w-4 mr-1" />
            Create Question Banks
          </Button>
        </div>
      </header>

      {/* Search Section */}
      <div className="mb-6">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search question banks..."
              value={search}
              onChange={handleSearchChange}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* User Question Banks */}
      <div className="mb-6">
        <QuestionBankSection
          title="User Question Banks"
          questionBanks={questionBanks}
          users={questionBankCreatedUser}
          uniqueUsers={uniqueUsers}
          showSection={showUserCard}
          setShowSection={setShowUserCard}
          onView={(q) =>
            navigate("/admin/question_bank/questions", {
              state: { _id: q._id, name: q.name },
            })
          }
          onDelete={(id, name) => {
            setShowDeletePopup(true);
            setDeleteId(id);
            setDeleteBankName(name);
          }}
          convertISOtoLocale={convertISOtoLocale}
          isSearching={isSearching}
          search={search}
          searchResults={searchQuestionBank}
          currentPage={userCurrentPage}
          setCurrentPage={setUserCurrentPage}
          itemsPerPage={itemsPerPage}
        />
      </div>

      {/* Company Question Banks */}
      <div className="mb-6">
        <QuestionBankSection
          title="Company Question Banks"
          questionBanks={userCompanyQuestionBank}
          users={companyUser}
          uniqueUsers={uniqueUsers}
          showSection={showCompanyCard}
          setShowSection={setShowCompanyCard}
          onView={(q) =>
            navigate("/admin/question_bank/questions", {
              state: { _id: q._id, name: q.name },
            })
          }
          onDelete={(id, name) => {
            setShowDeletePopup(true);
            setDeleteId(id);
            setDeleteBankName(name);
          }}
          convertISOtoLocale={convertISOtoLocale}
          isSearching={isSearching}
          search={search}
          searchResults={searchCompanyQuestionBank}
          currentPage={companyCurrentPage}
          setCurrentPage={setCompanyCurrentPage}
          itemsPerPage={itemsPerPage}
        />
      </div>

      {/* Purchased Question Banks */}
      <div className="mb-6">
        <QuestionBankSection
          title="Purchased Question Banks"
          questionBanks={purchasedQuestionBanks}
          users={purchasedUser}
          uniqueUsers={uniqueUsers}
          showSection={showPurchasedCard}
          setShowSection={setShowPurchasedCard}
          onView={(q) =>
            navigate("/admin/question_bank/questions", {
              state: { _id: q._id, name: q.name },
            })
          }
          onDelete={() => {}}
          convertISOtoLocale={convertISOtoLocale}
          isSearching={isSearching}
          search={search}
          searchResults={searchPurchasedQuestionBank}
          hideDelete={true}
          currentPage={purchasedCurrentPage}
          setCurrentPage={setPurchasedCurrentPage}
          itemsPerPage={itemsPerPage}
        />
      </div>

      {/* Modals */}
      <CreateQuestionBankModal
        open={showCreatePopup}
        onOpenChange={setShowCreatePopup}
        userCategories={userCategories}
        companyCategories={companyCategories}
        questionTypes={questionTypes}
        fetchFilteredCategories = {fetchFilteredCategories}
        onSuccess={fetchQuestionBank}
        showToast={showToast}
      />

      <SampleDownloadModal
        open={samplePopupOpen}
        onOpenChange={setSamplePopupOpen}
        userCategories={userCategories}
        companyCategories={companyCategories}
      />

      <UploadQuestionBanksModal
        open={uploadPopupOpen}
        onOpenChange={setUploadPopupOpen}
        dropDownQuestionBank={dropDownQuestionBank}
        onUploadSuccess={handleUploadSuccess}
        showToast={showToast}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={showDeletePopup}
        onOpenChange={setShowDeletePopup}
        onConfirmDelete={handleDelete}
        questionBankName={deleteBankName}
        isLoading={deleteLoading}
      />
    </div>
  );
}
